/**
 * Match note modal body: read-only view of invoice part ↔ NEX part and reason.
 */
import React from 'react';
import { Box, Flex, Icon, Text, VStack } from '@chakra-ui/react';
import { TbArrowDown } from 'react-icons/tb';
import type { InvoicePart, InvoiceType } from '../../types/invoice';
import { getSignedNexLineDisplayQty, type NexLineQuantityBasis } from '../../utils/nexLineQuantity';
import { LineCard, type LineCardLine } from './LineCard';

const ACCENT = '#0071E3';

export type MatchNoteNexLine = {
    partDesc?: string;
    description?: string;
    partNumber?: string;
    qtyRec?: number;
    qtyReturned?: number;
    qtyOnOrder?: number;
    qty?: number;
    quantity?: number;
    unitCost?: number;
    unit_price?: number;
    extendedCost?: number;
    extended_cost?: number;
    listPrice?: number;
    list_price?: number;
};

export interface ReconciliationMatchNoteContentProps {
    matchNote: {
        invoicePartNumber: string;
        nexPartNumber: string;
        reason: string;
        partIndex?: number;
        extractedFromPage?: {
            qty?: number;
            listPrice?: number;
            unitBuyPrice?: number;
            totalBuyPrice?: number;
        };
    };
    invoicePart: InvoicePart | null;
    invoiceType?: InvoiceType;
    nexLineQuantityBasis?: NexLineQuantityBasis;
    nexLine: MatchNoteNexLine | null;
    /**
     * When one extracted invoice line was expanded into multiple rows to match NEX, show the
     * original pre-split line (qty/total as on the document) and all matched NEX lines below.
     */
    matchNoteExpansion?: { originalInvoiceLine: InvoicePart; nexLinesInOrder: MatchNoteNexLine[] } | null;
}

function normPn(v: string | undefined): string {
    return (v ?? '').replace(/[\s\-*]/g, '').toLowerCase();
}

export function ReconciliationMatchNoteContent(props: ReconciliationMatchNoteContentProps) {
    const { matchNote, invoicePart, nexLine, invoiceType, matchNoteExpansion, nexLineQuantityBasis = 'qtyRec' } = props;
    const mn = matchNote;
    const expansion = matchNoteExpansion?.nexLinesInOrder?.length ? matchNoteExpansion : null;

    const invoiceLineData: LineCardLine | null = invoicePart
        ? {
              title: expansion ? expansion.originalInvoiceLine.title : invoicePart.title,
              partNumber: mn.invoicePartNumber,
              qty: expansion ? expansion.originalInvoiceLine.qty : invoicePart.qty,
              listPrice: expansion ? expansion.originalInvoiceLine.listPrice : invoicePart.listPrice,
              unitBuyPrice: expansion ? expansion.originalInvoiceLine.unitBuyPrice : invoicePart.unitBuyPrice,
              totalBuyPrice: expansion ? expansion.originalInvoiceLine.totalBuyPrice : invoicePart.totalBuyPrice,
          }
        : null;

    const nexLineData: LineCardLine | null =
        nexLine && !expansion
            ? {
                  title: nexLine.partDesc ?? nexLine.description,
                  partNumber: nexLine.partNumber,
                  qty: getSignedNexLineDisplayQty(nexLine, invoiceType, nexLineQuantityBasis),
                  listPrice: nexLine.listPrice ?? nexLine.list_price ?? undefined,
                  unitBuyPrice: nexLine.unitCost ?? nexLine.unit_price,
                  totalBuyPrice: nexLine.extendedCost ?? nexLine.extended_cost,
              }
            : null;

    const highlightPartNumber =
        normPn(mn.invoicePartNumber) !== normPn(mn.nexPartNumber);
    const diffFields = highlightPartNumber
        ? new Set<keyof LineCardLine>(['partNumber'] as const)
        : new Set<keyof LineCardLine>();

    const extractedFromPage = mn.extractedFromPage;
    const extractedLineData: LineCardLine | null = extractedFromPage
        ? {
              title: invoicePart?.title,
              partNumber: mn.invoicePartNumber,
              qty: extractedFromPage.qty,
              listPrice: extractedFromPage.listPrice,
              unitBuyPrice: extractedFromPage.unitBuyPrice,
              totalBuyPrice: extractedFromPage.totalBuyPrice,
          }
        : null;

    const rivianExtractionCorrection = !!extractedLineData && !!invoiceLineData;
    const extractedDiffFields = rivianExtractionCorrection
        ? new Set<keyof LineCardLine>(['qty', 'unitBuyPrice'] as const)
        : new Set<keyof LineCardLine>();
    const correctedDiffFields = rivianExtractionCorrection
        ? new Set<keyof LineCardLine>(['qty', 'unitBuyPrice'] as const)
        : diffFields;

    return (
        <VStack spacing={5} align="stretch">
            <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" px={4} py={3}>
                <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wider" mb={1}>Reason</Text>
                <Text fontSize="sm" color="gray.700">{mn.reason}</Text>
            </Box>
            {rivianExtractionCorrection && extractedLineData && invoiceLineData && (
                <Box bg="gray.50" borderRadius="xl" p={6}>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                As read from invoice
                            </Text>
                            <LineCard line={extractedLineData} highlightFields={extractedDiffFields} invoiceType={invoiceType} nexLineQuantityBasis={nexLineQuantityBasis} />
                        </Box>
                        <Flex justify="center"><Icon as={TbArrowDown as React.ElementType} boxSize={6} color={ACCENT} /></Flex>
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="blue.500" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                Corrected line item
                            </Text>
                            <LineCard line={invoiceLineData} highlightFields={correctedDiffFields} invoiceType={invoiceType} nexLineQuantityBasis={nexLineQuantityBasis} />
                        </Box>
                    </VStack>
                </Box>
            )}
            {!rivianExtractionCorrection && invoiceLineData && (expansion ? expansion.nexLinesInOrder.length > 0 : !!nexLineData) && (
                <Box bg="gray.50" borderRadius="xl" p={6}>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                {expansion ? 'Invoice line item (as on document)' : 'Invoice line item'}
                            </Text>
                            <LineCard line={invoiceLineData} highlightFields={diffFields} invoiceType={invoiceType} nexLineQuantityBasis={nexLineQuantityBasis} />
                        </Box>
                        <Flex justify="center"><Icon as={TbArrowDown as React.ElementType} boxSize={6} color={ACCENT} /></Flex>
                        {expansion ? (
                            <Box>
                                <Text fontSize="xs" fontWeight="bold" color="blue.500" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                    Matched NEX line items ({expansion.nexLinesInOrder.length})
                                </Text>
                                <VStack spacing={3} align="stretch">
                                    {expansion.nexLinesInOrder.map((nl, i) => (
                                        <LineCard
                                            key={i}
                                            invoiceType={invoiceType}
                                            nexLineQuantityBasis={nexLineQuantityBasis}
                                            line={{
                                                title: nl.partDesc ?? nl.description,
                                                partNumber: nl.partNumber,
                                                qty: getSignedNexLineDisplayQty(nl, invoiceType, nexLineQuantityBasis),
                                                listPrice: nl.listPrice ?? nl.list_price ?? undefined,
                                                unitBuyPrice: nl.unitCost ?? nl.unit_price,
                                                totalBuyPrice: nl.extendedCost ?? nl.extended_cost,
                                            }}
                                        />
                                    ))}
                                </VStack>
                            </Box>
                        ) : (
                            nexLineData && (
                                <Box>
                                    <Text fontSize="xs" fontWeight="bold" color="blue.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Matched NEX line item</Text>
                                    <LineCard line={nexLineData} highlightFields={diffFields} invoiceType={invoiceType} nexLineQuantityBasis={nexLineQuantityBasis} />
                                </Box>
                            )
                        )}
                    </VStack>
                </Box>
            )}
        </VStack>
    );
}
